import { Dropdown, Icon, Icons, Search, TableColumn, Tooltip } from '@silverfort/sf-components';
import { httpPost, removeTrailingTags } from '../../../../utils';
import { getBaseColumns, STATUS_FILTER_ITEMS } from '../../ComponentsOverviewUtils';
import { DcTableColumn, NodeTableColumn, SoftwareStatus } from '../../types';
import { NODES_TYPES } from '../../EnvironmentOverviewUtils';
import { Banner } from '../../components/Banner';

export const getDcsColumns = (softwareStatus: SoftwareStatus): TableColumn<DcTableColumn>[] => {
    return [
        ...getBaseColumns(softwareStatus),
        {
            columnKey: 'az',
            title: 'AZ',
            render: (item) => (Array.isArray(item.az) ? item.az.join(', ') : item.az || 'N/A')
        },
        {
            columnKey: 'protected',
            title: 'Protected',
            render: (item) =>
                item.protected ? (
                    <Icon size={24} icon={Icons.Check} className="text-sf-positive-500" />
                ) : (
                    <Icon size={24} icon={Icons.Remove} className="text-sf-gray-300" />
                )
        }
    ];
};

export const getNodesColumns = (softwareStatus: SoftwareStatus): TableColumn<NodeTableColumn>[] => {
    return [
        ...getBaseColumns(softwareStatus),
        {
            columnKey: 'az',
            title: 'AZ',
            render: (item) =>
                Array.isArray(item.az) ? (
                    <div className="font-sf">
                        <Tooltip
                            title={
                                item.az.slice(1).length > 0 && (
                                    <div className="flex flex-col">
                                        {item.az.slice(1).map((az) => (
                                            <div key={az}>{az}</div>
                                        ))}
                                    </div>
                                )
                            }>
                            <div className="truncate">
                                {item.az[0]}{' '}
                                {item.az.length > 1 && (
                                    <span className="text-11 font-bold">(+{item.az.length - 1})</span>
                                )}
                            </div>
                        </Tooltip>
                    </div>
                ) : (
                    item.az || 'N/A'
                )
        },
        {
            columnKey: 'domains',
            title: 'Protected domains',
            render: (item) =>
                Array.isArray(item.domains) ? (
                    <div className="font-sf">
                        <Tooltip
                            title={
                                item.domains.slice(1).length > 0 && (
                                    <div className="flex flex-col">
                                        {item.domains.slice(1).map((domain) => (
                                            <div key={domain}>{domain}</div>
                                        ))}
                                    </div>
                                )
                            }>
                            <div className="truncate">
                                {item.domains[0]}{' '}
                                {item.domains.length > 1 && (
                                    <span className="text-11 font-bold">(+{item.domains.length - 1})</span>
                                )}
                            </div>
                        </Tooltip>
                    </div>
                ) : (
                    item.domains || 'N/A'
                )
        }
    ];
};

export const handleUpgrade = async ({
    setUpgradeActivated,
    setIsGradualUpgrade,
    componentsType,
    selectedComponents,
    isGradualUpgrade
}) => {
    setUpgradeActivated(true);

    try {
        switch (componentsType) {
            case NODES_TYPES.PUSH_SERVICES:
                await httpPost(`/softwareUpdates/upgradePushServices`, {
                    selectedComponents
                });
                break;
            case NODES_TYPES.NODES:
                await httpPost(`/softwareUpdates/upgradeNodes`, {
                    selectedComponents,
                    isGradualUpgrade
                });
                break;
            case NODES_TYPES.DCS:
                await httpPost(`/softwareUpdates/upgradeDcs`, { selectedComponents });
                break;
            default:
                break;
        }
    } catch (error) {
        console.error(`Error upgrading ${componentsType}:`, error);
        setUpgradeActivated(false);
        setIsGradualUpgrade(false);
    }
};

export const stopGradualUpgrade = async () => {
    try {
        const response = (await httpPost(`/softwareUpdates/stopGradualUpgrade`)) as unknown as {
            data: { queuedComponents: string[] };
        };
        return response?.data?.queuedComponents;
    } catch (error) {
        console.error(`Error stopping gradual upgrade:`, error);
    }
};

export const getComponentName = (type: string) => {
    switch (type) {
        case NODES_TYPES.PUSH_SERVICES:
            return 'Desktop Messaging Service';
        case NODES_TYPES.NODES:
            return 'Node';
        case NODES_TYPES.DCS:
            return 'DC';
        default:
            return 'Component';
    }
};

export const buildComponentsStepFilters = ({
    components,
    setSelectedFilters,
    selectedFilters,
    setSearchTerm,
    componentsType,
    searchTerm
}) => {
    const versionValues = Array.from(new Set(components.map((comp) => comp.version)));
    const azValues = Array.from(new Set(components.map((comp) => (comp.az ? comp.az : [])).flat()));
    const domainsValues = Array.from(new Set(components.map((comp) => (comp.domains ? comp.domains : [])).flat()));

    return (
        <div className="h-8 w-full flex justify-between items-center">
            <div className="flex gap-2">
                <Dropdown
                    removable={selectedFilters.version.length > 0}
                    onRemove={() => setSelectedFilters((prev) => ({ ...prev, version: [] }))}
                    title="Current version"
                    items={versionValues.map((version) => ({
                        value: version,
                        title: removeTrailingTags(version)
                    }))}
                    multiple
                    onChange={(values) => setSelectedFilters((prev) => ({ ...prev, version: values }))}
                    variant="filter"
                    selectedItems={selectedFilters.version}
                />
                <Dropdown
                    removable={selectedFilters.status.length > 0}
                    onRemove={() => setSelectedFilters((prev) => ({ ...prev, status: [] }))}
                    title="Status"
                    items={Object.values(STATUS_FILTER_ITEMS)}
                    multiple
                    onChange={(values) => setSelectedFilters((prev) => ({ ...prev, status: values }))}
                    variant="filter"
                    selectedItems={selectedFilters.status}
                />
                {componentsType !== NODES_TYPES.PUSH_SERVICES && (
                    <Dropdown
                        removable={selectedFilters.az.length > 0}
                        onRemove={() => setSelectedFilters((prev) => ({ ...prev, az: [] }))}
                        title="AZ"
                        items={azValues.map((az) => ({
                            value: az as string,
                            title: az as string
                        }))}
                        multiple
                        onChange={(values) => setSelectedFilters((prev) => ({ ...prev, az: values }))}
                        variant="filter"
                        selectedItems={selectedFilters.az}
                    />
                )}
                {componentsType === NODES_TYPES.NODES && (
                    <Dropdown
                        removable={selectedFilters.domains.length > 0}
                        onRemove={() => setSelectedFilters((prev) => ({ ...prev, domains: [] }))}
                        title="Protected domain"
                        items={domainsValues.map((domain) => ({
                            value: domain as string,
                            title: domain as string
                        }))}
                        multiple
                        onChange={(values) => setSelectedFilters((prev) => ({ ...prev, domains: values }))}
                        variant="filter"
                        selectedItems={selectedFilters.domains}
                    />
                )}
                {componentsType === NODES_TYPES.DCS && (
                    <Dropdown
                        removable={selectedFilters.is_protected.length > 0}
                        onRemove={() => setSelectedFilters((prev) => ({ ...prev, is_protected: [] }))}
                        title="Protected"
                        items={[
                            { value: true, title: 'Protected' },
                            { value: false, title: 'Unprotected' }
                        ]}
                        multiple
                        onChange={(values) => setSelectedFilters((prev) => ({ ...prev, is_protected: values }))}
                        variant="filter"
                        selectedItems={selectedFilters.is_protected}
                    />
                )}
            </div>
            <div className="flex items=center gap-4">
                <Search
                    border
                    text={searchTerm}
                    placeholder="Search by Name"
                    onChange={(value) => setSearchTerm(value)}
                />
            </div>
        </div>
    );
};

export const getUpgradingFieldByComponentsType = (componentsType: string): string => {
    switch (componentsType) {
        case NODES_TYPES.PUSH_SERVICES:
            return 'upgradingPushServices';
        case NODES_TYPES.NODES:
            return 'upgradingNodes';
        case NODES_TYPES.DCS:
            return 'upgradingDcs';
        default:
            return '';
    }
};

export const ShouldUpgradeAryaBanner = (
    <div className="pb-4">
        <Banner theme={'Attention'}>
            <Banner.Body content={'The Nodes cannot be upgraded until the DMSs are upgraded.'} />
        </Banner>
    </div>
);

export const AutoDcUpgradeInfo = (
    <div className="flex flex-col gap-3">
        <div className="text-18 font-sf-poppins text-sf-gray-800">DCs are being upgraded automatically</div>
        <div className="text-13 text-sf-dark-500 font-sf">
            <div>DCs are being upgraded automatically When the upgrade completes</div>
            <div>Click Finish to close the wizard.</div>
        </div>
    </div>
);

export const RecommendGradualUpgradeBanner = (
    <div className="pb-4">
        <Banner theme={'Info'}>
            <Banner.Body content={'Silverfort recommends selecting all nodes and then enabling Sequential upgrade.'} />
        </Banner>
    </div>
);

export const DcsUpgradeDisclaimerBanner = (
    <div className="pb-4">
        <Banner theme={'Attention'}>
            <Banner.Body
                content={
                    'DCs can only be upgraded to a new major version after their associated node has been upgraded. Attempting to upgrade DCs before their nodes will result in failure.'
                }
            />
        </Banner>
    </div>
);
